import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Chip, Box } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useLazyListRealTimeTrackingQuery } from "store/services/realTimeTrackingApi";

import { useAppMeta, useTablePagination } from "hooks";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";

const getStatusStyle = (status) => {
  const styles = {
    "ON THE WAY": { backgroundColor: "#EBF5FB", textColor: "#3498DB" },
    DELIVERED: { backgroundColor: "#DFF7DF", textColor: "green" },
    PENDING: { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" },
    CANCELED: { backgroundColor: "#FDEDEC", textColor: "#E74C3C" },
  };

  return styles[status?.toUpperCase()] || { backgroundColor: "#F8F9F9", textColor: "#7F8C8D" };
};

const RealTimeTrackingListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [{ title: "Real-Time Tracking" }];

  const trackingListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" }
  ];

  const columns = [
    {
      name: "Tracking ID",
      selector: (row) => row.trackingId,
      cell: (row) => (
        <Link
          title={row?.trackingId}
          to={`/app/crm/realtime-tracking/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.trackingId ? (
            <HighlightedText text={row?.trackingId} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
    },
    {
      name: "Vehicle ID",
      selector: (row) => row.vehicle?.vehicle_id,
      cell: (row) => row.vehicle?.vehicle_id || "N/A",
    },
    {
      name: "Location",
      selector: (row) => `${row.location?.lat}, ${row.location?.lon}`,
      cell: (row) =>
        row.location
          ? `Lat: ${row.location.lat}, Lon: ${row.location.lon}`
          : "Unknown",
    },
    {
      name: "Timestamp",
      selector: (row) => <AppTableDateTime date={row?.timestamp} />,
      // cell: (row) => localizeDateTimeShort(row?.timestamp),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Chip
            label={row?.status?.toUpperCase() || "N/A"}
            style={{
              backgroundColor: getStatusStyle(row?.status).backgroundColor,
              color: getStatusStyle(row?.status).textColor,
              fontWeight: "bold",
              fontSize: "0.7rem",
              height: "20px",
              padding: "0 6px",
              borderRadius: "12px",
              width: "auto",
            }}
          />
        </Box>
      ),
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/realtime-tracking/${row._id}/detail`}>
            <IconButton size="small" aria-label="View Tracking" color="primary">
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/realtime-tracking/${row._id}/edit`}>
                      <IconButton size="small" aria-label="Edit Fulfillment">
                        <ModeEditOutlineTwoTone />
                      </IconButton>
                    </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [fetchTrackings, { data: trackingRes, isLoading, isFetching, error }] =
    useLazyListRealTimeTrackingQuery();

  React.useEffect(() => {
    fetchTrackings({
      active: activeTab === "Active",
      status: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchTrackings, keyword, perPage]);

  useAppMeta(`${activeTab} realtime Tracking`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={trackingListTabs} activeTab={activeTab} />
        <TableFilter searchLabel="Search Trackings" disabled={isLoading || isFetching} />
        <AppTable
          columns={columns}
          data={trackingRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={trackingRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default RealTimeTrackingListScreen;

const PageActions = () => (
  <Link to="/app/crm/realtime-tracking/create">
    <Button startIcon={<Add />} variant="contained">
      New Tracking
    </Button>
  </Link>
);
